import { useCallback, useEffect, useRef, useState } from "react";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSIconButton from "views/system-design/DSIconButton";
import DSSecondaryButton from "views/system-design/DSSecondaryButton";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import { Attachment, Ticket } from "../../types";
import { useTickets } from "../../context/TicketsContext";
import {
  generateId,
  formatFileSize,
  isImage,
  isVideo,
  fileToBase64,
} from "../../utils";
import uploadFile from "utils/uploadFiles";

interface TicketAttachmentsProps {
  ticket: Ticket;
}

function AttachmentPreview({ attachment }: { attachment: Attachment }) {
  if (!attachment.url) {
    return (
      <InsertDriveFileIcon sx={{ fontSize: 20, color: "text.secondary" }} />
    );
  }

  if (isImage(attachment.type)) {
    return (
      <img
        src={attachment.url}
        alt={attachment.name}
        style={{
          width: 120,
          height: 80,
          objectFit: "cover",
          borderRadius: 4,
          cursor: "pointer",
        }}
        onClick={(event) => {
          event.stopPropagation();
          window.open(attachment.url, "_blank");
        }}
      />
    );
  }

  if (isVideo(attachment.type)) {
    return (
      <video
        src={attachment.url}
        controls
        style={{
          width: 200,
          maxHeight: 140,
          borderRadius: 4,
        }}
        onClick={(event) => event.stopPropagation()}
      />
    );
  }

  return (
    <DSBox
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        cursor: "pointer",
      }}
      onClick={(event: React.MouseEvent) => {
        event.stopPropagation();
        window.open(attachment.url, "_blank");
      }}
    >
      <InsertDriveFileIcon sx={{ fontSize: 20, color: "text.secondary" }} />
    </DSBox>
  );
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

export default function TicketAttachments({ ticket }: TicketAttachmentsProps) {
  const { addAttachment, deleteAttachment } = useTickets();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;

      const oversized = files.find((file) => file.size > MAX_FILE_SIZE);
      if (oversized) {
        setUploadError(`File "${oversized.name}" exceeds the 10 MB limit.`);
        return;
      }

      setUploading(true);
      setUploadError(null);
      try {
        for (const file of files) {
          const base64 = await fileToBase64(file);
          const url = await uploadFile({
            file: base64,
            folder: `tickets/${ticket.id}`,
          });
          const attachment: Attachment = {
            id: generateId(),
            name: file.name,
            size: file.size,
            type: file.type,
            url,
          };
          await addAttachment(ticket.id, attachment);
        }
      } catch {
        setUploadError("Failed to upload file. Please try again.");
      } finally {
        setUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    },
    [ticket.id, addAttachment],
  );

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    uploadFiles(Array.from(files));
  };

  useEffect(() => {
    const handlePaste = (event: ClipboardEvent) => {
      const clipboardData = event.clipboardData;
      if (!clipboardData) return;

      const files: File[] = [];

      if (clipboardData.files.length > 0) {
        files.push(...Array.from(clipboardData.files));
      } else if (clipboardData.items) {
        for (const item of Array.from(clipboardData.items)) {
          if (item.kind === "file") {
            const file = item.getAsFile();
            if (file) files.push(file);
          }
        }
      }

      if (files.length === 0) return;

      const namedFiles = files.map((file) => {
        if (!file.name || file.name === "image.png") {
          return new File([file], `pasted-image-${Date.now()}.png`, {
            type: file.type,
          });
        }
        return file;
      });

      uploadFiles(namedFiles);
    };

    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [uploadFiles]);

  const handleDelete = (attachmentId: string) => {
    deleteAttachment(ticket.id, attachmentId);
  };

  return (
    <DSBox sx={{ mt: 3 }}>
      <DSBox sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <DSTypography variant="subtitle2" sx={{ color: "text.secondary" }}>
          Attachments ({ticket.attachments.length})
        </DSTypography>
      </DSBox>

      {ticket.attachments.length > 0 && (
        <DSBox
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            mb: 1,
          }}
        >
          {ticket.attachments.map((attachment) => (
            <DSBox
              key={attachment.id}
              sx={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                p: 1,
                bgcolor: "white.5",
                borderRadius: 1,
                maxWidth: 220,
                "&:hover .delete-btn": { opacity: 1 },
              }}
            >
              <AttachmentPreview attachment={attachment} />
              <DSBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {!isImage(attachment.type) && !isVideo(attachment.type) && (
                  <InsertDriveFileIcon
                    sx={{ fontSize: 16, color: "text.secondary" }}
                  />
                )}
                <DSBox sx={{ flex: 1, minWidth: 0 }}>
                  <DSTypography
                    variant="caption"
                    noWrap
                    title={attachment.name}
                  >
                    {attachment.name}
                  </DSTypography>
                  <DSTypography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      display: "block",
                      fontSize: 10,
                    }}
                  >
                    {formatFileSize(attachment.size)}
                  </DSTypography>
                </DSBox>
                <DSIconButton
                  className="delete-btn"
                  size="small"
                  onClick={() => handleDelete(attachment.id)}
                  sx={{ opacity: 0, transition: "opacity 0.2s" }}
                >
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </DSIconButton>
              </DSBox>
            </DSBox>
          ))}
        </DSBox>
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileSelect}
        style={{ display: "none" }}
      />
      <DSSecondaryButton
        size="small"
        startIcon={
          uploading ? <DSCircularProgress size={16} /> : <AttachFileIcon />
        }
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
      >
        {uploading ? "Uploading..." : "Add Attachment"}
      </DSSecondaryButton>
      {uploadError && (
        <DSTypography
          variant="caption"
          sx={{ color: "error.main", mt: 0.5, display: "block" }}
        >
          {uploadError}
        </DSTypography>
      )}
    </DSBox>
  );
}
